import React, { useCallback, useRef } from 'react'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useDispatch } from 'APP/Hooks/redux'
import { memberTasks } from 'APP/Store/MemberTasks'
import { trackMemberTask } from 'APP/Components/MemberTaskBar/analytics'
import { logger } from 'APP/Lib/Datadog'
import { RootScreens } from 'APP/Lib/Enums'
import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'
import { getMemberTaskDefinition } from 'APP/Components/MemberTaskBar/memberTaskDefinitions'
import Header, { HeaderProps } from 'APP/Converse/Header'

export type NextMemberTaskScreenRouteProp = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'nextMemberTaskScreen'
>

const Container = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`

const HeaderContainer = styled(SafeAreaView).attrs({
  edges: ['top'],
})`
  background-color: ${({ theme }) => theme.colors.elementsBg};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
`

export const NextMemberTaskScreenHeader = (props: HeaderProps & { options: HeaderProps }) => (
  <HeaderContainer>
    <Header {...props} {...props.options} isDrawer />
  </HeaderContainer>
)

/**
 * Single entry point for any member task:
 * maps the experience to its corresponding view component.
 */
const NextMemberTaskScreen = ({ route, navigation }: NextMemberTaskScreenRouteProp) => {
  const { task } = route.params
  const dispatch = useDispatch()
  const hasSucceeded = useRef(false)

  const forceRefresh = useCallback(
    () =>
      dispatch(
        memberTasks.fetchTasks({ scope: 'episode', episodeId: task.episode_id, force: true })
      ),
    [dispatch, task.episode_id]
  )

  const handleSuccess = useCallback(() => {
    hasSucceeded.current = true
    trackMemberTask('completed', task)
    forceRefresh()
  }, [task, forceRefresh])

  const handleError = useCallback(
    (error: unknown) => {
      trackMemberTask('failed', task)
      forceRefresh()
      logger.error(
        'Member task failed',
        { taskType: task.task_experience, episodeId: task.episode_id },
        error as Error
      )
    },
    [task, forceRefresh]
  )

  const handleClose = useCallback(() => {
    if (!hasSucceeded.current) trackMemberTask('dismissed', task)
    if (navigation.canGoBack()) navigation.goBack()
    else NestedNavHelper.reset({ index: 0, routes: [{ name: RootScreens.today }] })
  }, [task, navigation])

  const { View: MemberTaskView } = getMemberTaskDefinition(task.task_experience)

  return (
    <Container>
      <MemberTaskView
        memberTask={task}
        onSuccess={handleSuccess}
        onError={handleError}
        onClose={handleClose}
      />
    </Container>
  )
}

export default NextMemberTaskScreen
